<template>
  <div class="flex flex-col gap-6 animate-fade-in">
    <!-- Show PIN Input when we have payload but no decrypted mnemonic -->
    <template v-if="!hasDecryptedData">
      <p class="text-center text-vault-text-secondary mb-4">
        {{ unlockMessage || $t('wallet.enterPin') }}
      </p>

      <EmojiPinInput
        ref="pinInput"
        :length="pinLength"
        :disabled="isDecrypting"
        :error="pinError"
        @complete="handlePinComplete"
      />

      <p v-if="isDecrypting" class="text-center text-sm text-vault-blue-400 font-semibold mt-4">
        {{ $t('wallet.decrypting') }}
      </p>
    </template>

    <!-- Show Wallet Content when unlocked -->
    <template v-else>
      <!-- Balance View: Show gift card and action buttons -->
      <template v-if="internalStep === 'balance'">
        <!-- Gift Card Display -->
        <div class="w-full mb-6">
          <GiftCard
            mode="active"
            :balance="balanceDisplay"
            :currency="currency"
            :bg-style="bgStyle"
            :message="message"
            :loading="loading"
            :is-refreshing="isRefreshing"
            :wallet-address="walletAddress"
            :network-id="walletChain"
            :mnemonic="mnemonic"
            :claim-url="claimUrl"
            :emoji-pin="currentEmojiPin"
            @refresh="refreshBalance"
          />
        </div>

        <!-- Action Buttons -->
        <ClaimActions
          :address="walletAddress"
          :can-withdraw="isWithdrawReady"
          :loading="loading"
          :claim-url="claimUrl"
          :emoji-pin="currentEmojiPin"
          :show-address="false"
          @lock="handleLock"
          @deposit="goToDeposit"
          @withdraw="startWithdrawal"
        />

        <!-- Mnemonic Reveal (optional) -->
        <MnemonicReveal
          v-if="showMnemonic"
          :mnemonic="mnemonic"
          context="active"
          @copy="copyMnemonic"
          class="mt-4"
        />
      </template>

      <!-- Deposit View -->
      <WalletDeposit
        v-if="internalStep === 'deposit'"
        :address="walletAddress"
        :currency="currency?.code || 'ETH'"
        :currency-info="currency"
        :loading="loading"
        :rpc-url="rpcUrl"
        :chain="walletChain"
        @cancel="goBackToBalance"
        @deposit-received="handleDepositReceived"
        @transaction-started="handleTransactionStarted"
        @transaction-completed="handleTransactionCompleted"
      />

      <!-- Withdrawal Form -->
      <WalletWithdrawal
        v-if="internalStep === 'destination'"
        :balance="balanceDisplay"
        :currency="currency?.code || 'ETH'"
        :is-gasless="isGaslessSupported"
        :loading="loading"
        @submit="handleWithdrawalSubmit"
        @cancel="goBackToBalance"
      />

      <!-- Transferring Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'transferring'"
        status="loading"
        :title="$t('claimView.transferring.title')"
        :message="$t('claimView.transferring.message')"
      />

      <!-- Completed Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'completed'"
        status="success"
        :title="$t('claimView.completed.title')"
      >
        <template #details>
          <div class="flex flex-col gap-3 p-4 bg-vault-bg-secondary rounded-lg">
            <div class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.amount') }}</span>
              <strong class="text-vault-text-primary font-semibold">{{ transferResult?.amount }} {{ currency?.code || 'ETH' }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.to') }}</span>
              <code class="text-vault-text-primary font-semibold font-mono text-xs break-all">{{ transferResult?.to }}</code>
            </div>
            <div v-if="txUrl" class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.transaction') }}</span>
              <a :href="txUrl" target="_blank" class="text-vault-purple-400 no-underline font-semibold hover:underline">{{ $t('claimView.completed.viewOnExplorer') }}</a>
            </div>
          </div>

          <MnemonicReveal
            v-if="showMnemonic"
            :mnemonic="mnemonic"
            context="spent"
            @copy="copyMnemonic"
          />
        </template>

        <template #actions>
          <button type="button" class="btn-primary" @click="returnToBalance">
            {{ $t('claimView.backToBalance') }}
          </button>
        </template>
      </WalletStatusScreen>

      <!-- Error Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'error'"
        status="error"
        :title="$t('claimView.error.title')"
        :message="sweeperErrorMessage"
      >
        <template #actions>
          <button type="button" class="btn-primary" :disabled="loading" @click="returnToBalance">
            {{ $t('claimView.backToBalance') }}
          </button>
          <button type="button" class="btn-secondary" :disabled="loading" @click="retryImport">
            {{ $t('claimView.error.retry') }}
          </button>
        </template>
      </WalletStatusScreen>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { decryptWithEmojiPin, decodeBase58 } from '@tokenbox/wallet-kit';
import { useWalletSweeper } from '../../composables/useWalletSweeper';
import { getNetworkConfig } from '../../config/currencies';
import { apiConfig } from '../../config/api';
import { useNotifications } from '../../composables/useNotifications';
import EmojiPinInput from '../ui/EmojiPinInput.vue';
import GiftCard from '../ui/GiftCard.vue';
import ClaimActions from './ClaimActions.vue';
import WalletWithdrawal from './WalletWithdrawal.vue';
import WalletDeposit from './WalletDeposit.vue';
import MnemonicReveal from './MnemonicReveal.vue';
import WalletStatusScreen from './WalletStatusScreen.vue';

const { t: $t } = useI18n();
const { showSuccess, showError } = useNotifications();

interface WalletMetadata {
  chain?: string;
  bgStyle?: number;
  privateMessage?: string;
}

interface CurrencyConfig {
  id: string;
  name: string;
  code: string;
  symbol: string;
  type: 'native' | 'erc20';
  decimals: number;
  contractAddress?: string;
  contractAddresses?: Record<string, string>;
  networks?: Array<{ networkId: string; contractAddress?: string }>;
}

const props = withDefaults(defineProps<{
  claimUrl: string;
  emojiPin?: string;
  currency?: CurrencyConfig;
  unlockMessage?: string;
}>(), {
  emojiPin: '',
  unlockMessage: ''
});

const emit = defineEmits<{
  'wallet-unlocked': [data: { mnemonic: string; metadata: WalletMetadata; emojiPin: string }];
  'wallet-locked': [];
  'transfer-complete': [result: any];
}>();

// Local state
const pinLength = 4;
const pinError = ref('');
const isDecrypting = ref(false);
const pinInput = ref<InstanceType<typeof EmojiPinInput> | null>(null);
const encodedPayload = ref<Uint8Array | null>(null);
const mnemonic = ref('');
const currentEmojiPin = ref('');
const walletChain = ref<string>('base_sepolia');
const showMnemonic = ref(false);
const isRefreshing = ref(false);
const internalStep = ref<'balance' | 'deposit' | 'destination' | 'transferring' | 'completed' | 'error'>('balance');

// Gift card metadata
const bgStyle = ref(0);
const message = ref('');

// Derived state
const hasDecryptedData = computed(() => !!mnemonic.value);

// Get default network from currency
const defaultNetworkId = computed(() => {
  const networks = props.currency?.networks;
  return networks && networks.length > 0 ? networks[0].networkId : 'base_sepolia';
});

// Network configuration
const resolvedNetwork = computed(() => getNetworkConfig(walletChain.value));
const fallbackNetwork = computed(() => getNetworkConfig(defaultNetworkId.value));

const rpcUrl = computed(() => {
  return resolvedNetwork.value?.rpcUrl
    || fallbackNetwork.value?.rpcUrl
    || 'https://ethereum-sepolia-rpc.publicnode.com';
});

const explorerBaseUrl = computed(() => {
  return resolvedNetwork.value?.explorerUrl
    || fallbackNetwork.value?.explorerUrl
    || null;
});

// Initialize wallet sweeper
const sweeper = useWalletSweeper({
  rpcUrl: rpcUrl.value,
  chain: walletChain.value,
  currency: props.currency as any,
  paymasterUrl: apiConfig.paymasterUrl,
});

const {
  wallet,
  destination: destinationRef,
  transferResult,
  error: sweeperError,
  loading,
  canProceed,
  setDestination,
  importWallet,
  fetchBalance,
  executeTransfer,
  executeGaslessTransfer,
  reset,
  updateConfig,
} = sweeper;

// Computed properties
const walletAddress = computed(() => wallet.value?.address ?? '');
const balanceDisplay = computed(() => {
  if (!wallet.value?.balance) return '0.0000';
  const numeric = Number.parseFloat(wallet.value.balance);
  return Number.isFinite(numeric) ? numeric.toFixed(4) : wallet.value.balance;
});

const isGaslessSupported = computed(() => {
  if (props.currency?.type !== 'erc20') return false;
  const supportedChains = new Set(['base_sepolia']);
  return supportedChains.has(walletChain.value);
});

const sweeperErrorMessage = computed(
  () => sweeperError.value?.message ?? $t('claimView.error.defaultMessage')
);

const txUrl = computed(() => {
  const hash = transferResult.value?.txHash;
  if (!hash) return null;
  const baseUrl = explorerBaseUrl.value;
  if (!baseUrl) return null;
  const sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${sanitizedBase}/tx/${hash}`;
});

const isWithdrawReady = computed(() => canProceed.value && !!wallet.value && !loading.value);

// Watch for config changes
watch(
  [walletChain, rpcUrl, () => props.currency],
  ([chain, url, currency]) => {
    updateConfig({
      chain,
      rpcUrl: url,
      currency: currency as any,
    });
  },
  { immediate: true }
);

// Decode payload from claimUrl on mount
onMounted(() => {
  if (!props.claimUrl) return;

  try {
    const hashIndex = props.claimUrl.indexOf('#');
    if (hashIndex === -1) return;

    const hash = props.claimUrl.slice(hashIndex + 1);
    encodedPayload.value = decodeBase58(hash);

    // If emojiPin is provided in props, auto-unlock
    if (props.emojiPin && encodedPayload.value) {
      handlePinComplete(props.emojiPin);
    }
  } catch (err) {
    console.error('[ClaimWallet] Failed to decode payload:', err);
  }
});

// Watch for emojiPin changes from parent
watch(() => props.emojiPin, (newPin) => {
  if (newPin && encodedPayload.value && !hasDecryptedData.value) {
    handlePinComplete(newPin);
  }
});

// PIN and unlock handling
async function handlePinComplete(pin: string) {
  if (!encodedPayload.value || isDecrypting.value) return;

  isDecrypting.value = true;
  pinError.value = '';

  let decryptedPayload: { mnemonic: string; metadata: WalletMetadata };
  try {
    decryptedPayload = await decryptWithEmojiPin(encodedPayload.value, pin);
  } catch (err) {
    pinError.value = $t('wallet.incorrectPin');
    if (pinInput.value) {
      pinInput.value.reset();
    }
    isDecrypting.value = false;
    return;
  }

  // Store decrypted data
  mnemonic.value = decryptedPayload.mnemonic;
  currentEmojiPin.value = pin;
  walletChain.value = decryptedPayload.metadata.chain || defaultNetworkId.value;
  bgStyle.value = decryptedPayload.metadata.bgStyle || 0;
  message.value = decryptedPayload.metadata.privateMessage || '';

  console.log('🔓 [ClaimWallet] Wallet unlocked with PIN:', pin);

  // Update sweeper config
  updateConfig({
    chain: walletChain.value,
    rpcUrl: rpcUrl.value,
    currency: props.currency as any,
  });

  // Import wallet
  try {
    await importWallet(decryptedPayload.mnemonic);
    internalStep.value = 'balance';
  } catch (err) {
    console.error('Wallet import failed:', err);
    internalStep.value = 'error';
  }

  isDecrypting.value = false;

  // Notify parent
  emit('wallet-unlocked', { ...decryptedPayload, emojiPin: pin });
}

function handleLock() {
  pinError.value = '';
  if (pinInput.value) {
    pinInput.value.reset();
  }

  // Clear sensitive data
  mnemonic.value = '';
  currentEmojiPin.value = '';
  showMnemonic.value = false;

  // Reset wallet state
  reset();
  internalStep.value = 'balance';

  emit('wallet-locked');
  console.log('🔒 [ClaimWallet] Wallet locked');
}

// Deposit flow
function goToDeposit() {
  if (loading.value) return;
  internalStep.value = 'deposit';
}

function handleTransactionStarted(data: any) {
  console.log('🔄 [ClaimWallet] Transaction started:', data);
  showSuccess('Payment initiated!');
}

function handleTransactionCompleted(data: any) {
  console.log('✅ [ClaimWallet] Transaction completed:', data);
  showSuccess('Payment completed! Monitoring deposit...');
}

async function handleDepositReceived(balance: any) {
  console.log('✅ [ClaimWallet] Deposit received:', balance);
  showSuccess(`Deposit received: ${balance.balanceEth} ${props.currency?.code || 'ETH'}`);

  // Refresh balance and return to balance view
  await refreshBalance();
  goBackToBalance();
}

// Withdrawal flow
function startWithdrawal() {
  if (!isWithdrawReady.value) return;
  internalStep.value = 'destination';
  destinationRef.value = '';
}

async function handleWithdrawalSubmit({ destination }: { destination: string }) {
  if (loading.value) return;

  const transferType = isGaslessSupported.value ? 'gasless' : 'regular';
  console.log(`🚀 [ClaimWallet] Starting ${transferType} transfer...`);

  try {
    setDestination(destination);
    destinationRef.value = destination;

    internalStep.value = 'transferring';

    // Execute the appropriate transfer method
    if (isGaslessSupported.value) {
      await executeGaslessTransfer();
    } else {
      await executeTransfer();
    }

    internalStep.value = 'completed';
    emit('transfer-complete', transferResult.value);
    console.log('✅ [ClaimWallet] Transfer completed');
  } catch (err: any) {
    console.error(`❌ [ClaimWallet] Transfer failed:`, err);
    showError(err.message || 'Transfer failed');
    internalStep.value = 'error';
  }
}

function goBackToBalance() {
  internalStep.value = 'balance';
  destinationRef.value = '';
}

// Balance refresh
async function refreshBalance() {
  isRefreshing.value = true;
  try {
    await fetchBalance();
  } catch (err) {
    console.error('Failed to refresh balance:', err);
    showError('Failed to refresh balance');
  } finally {
    setTimeout(() => {
      isRefreshing.value = false;
    }, 600);
  }
}

// Mnemonic reveal
function toggleMnemonic() {
  showMnemonic.value = !showMnemonic.value;
}

function copyMnemonic() {
  showSuccess('Mnemonic copied to clipboard');
}

// Error handling
async function retryImport() {
  if (!mnemonic.value) return;
  reset();
  internalStep.value = 'balance';
  destinationRef.value = '';
  try {
    await importWallet(mnemonic.value);
  } catch (err) {
    console.error('Retry import failed:', err);
    showError('Failed to import wallet');
    internalStep.value = 'error';
  }
}

async function returnToBalance() {
  loading.value = true;
  try {
    // Re-import wallet if it was wiped after transfer
    if (!wallet.value && mnemonic.value) {
      console.log('🔄 [ClaimWallet] Re-importing wallet to check balance...');
      await importWallet(mnemonic.value);
    } else {
      // Just refresh balance if wallet still exists
      await refreshBalance();
    }
    internalStep.value = 'balance';
  } catch (err) {
    console.error('Failed to return to balance view:', err);
    internalStep.value = 'balance';
  } finally {
    loading.value = false;
  }
}

// Expose methods for parent components
defineExpose({
  resetPinInput: () => {
    pinError.value = '';
    if (pinInput.value) {
      pinInput.value.reset();
    }
  }
});
</script>

<style scoped>
/* No custom styles - all handled by Tailwind */
</style>
